import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Pin every transcript row's `contain-intrinsic-size` to its measured height.
 *
 * Rows carry `content-visibility: auto`, so a row that has never been on
 * screen contributes its `contain-intrinsic-size` to the scroll height
 * instead of its real height. `auto <length>` means the browser's own
 * last-remembered size takes over once a row has rendered — but a
 * never-rendered row has no remembered size, and no fixed fallback can be
 * right for arbitrary content. A wrong fallback resizes the content above a
 * reader as they scroll up into it; Chromium hides that behind scroll
 * anchoring, Firefox does not anchor a `content-visibility` resize, so there
 * the viewport visibly jumps. This hook closes the gap by manufacturing the
 * remembered size eagerly: render each new row once, pre-paint, and pin what
 * it measured. After that, skipping and unskipping a row changes no layout in
 * any browser.
 *
 * Invariants the call site cannot see:
 *
 * - Each element is measured once (`measured` WeakSet), so a streaming commit
 *   pays for its new rows only — O(new rows), not O(window). The full pass
 *   runs exactly when the window changes shape: first mount and each
 *   "Show earlier messages" reveal. Those cost one forced layout of the new
 *   rows (~20-40ms for the initial window, ~70-100ms for a 4x reveal); keep
 *   that out of any per-commit path.
 * - Correctness rests on element identity: rows are keyed by message id, so a
 *   window that swaps to another agent's transcript produces new elements and
 *   they measure fresh. Recycling row elements across messages would silently
 *   pin stale heights.
 * - A child that already declares an inline `containIntrinsicSize` is left
 *   alone — the transcript's start/end sentinels pin themselves to `0px` so
 *   they never reserve a ghost gap, and that must win over measurement.
 * - Measured virtual-turn wrappers own their width-aware height pins. This
 *   hook skips them entirely: a distant spacer has no children to reflow, so
 *   measuring it after a width change would only preserve its stale height.
 * - A zero measurement means there is no layout to read (jsdom, hidden tab);
 *   the element stays unmarked so the next commit retries.
 * - Measurements are only valid for the width they were taken at. A content
 *   width change (sidebar, viewport) clears the set and re-measures once the
 *   width has settled, rather than on every animation frame.
 *
 * This owns ordinary transcript rows. `MeasuredVirtualTurn` is the sole
 * exception because it can temporarily remount its hidden content before
 * measuring a new width.
 */
export function useMeasuredRowSizes(
  contentRef: RefObject<HTMLElement | null>,
  rowsKey: unknown,
): void {
  const measuredRef = useRef<WeakSet<Element>>(new WeakSet());
  const measuredWidthRef = useRef<number | null>(null);

  const measureNewRows = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const pending: HTMLElement[] = [];
    for (const child of content.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.dataset.virtualTurn !== undefined) continue;
      if (measuredRef.current.has(child)) continue;
      if (child.style.containIntrinsicSize !== '') continue;
      pending.push(child);
    }
    if (pending.length === 0) return;

    for (const el of pending) el.style.contentVisibility = 'visible';
    // One synchronous reflow renders every pending row at its real height.
    void content.offsetHeight;
    const heights = pending.map((el) => el.getBoundingClientRect().height);
    for (let i = 0; i < pending.length; i++) {
      const el = pending[i];
      el.style.contentVisibility = '';
      if (heights[i] === 0) continue;
      el.style.containIntrinsicSize = `auto ${heights[i]}px`;
      measuredRef.current.add(el);
    }
    measuredWidthRef.current = content.getBoundingClientRect().width;
  }, [contentRef]);

  // Pre-paint, after the rows are in the DOM: the first frame a row is ever
  // part of already uses its true size. Declaration order matters — the
  // reveal flow restores `scrollHeight - scrollTop` in its own layout effect,
  // so this hook must be called before it in the component or the restore
  // does its arithmetic on unmeasured heights.
  useLayoutEffect(() => {
    measureNewRows();
  }, [measureNewRows, rowsKey]);

  // A pinned height is only right for the width it was measured at.
  // Re-measure when the content settles at a new width — "settles" because
  // sidebar and viewport animations stream continuous width changes, and a
  // full pass per animation frame would jank the drag.
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (typeof ResizeObserver === 'undefined' || !content) return;
    let settleTimer: number | null = null;
    const observer = new ResizeObserver(() => {
      const width = content.getBoundingClientRect().width;
      if (measuredWidthRef.current === null || width === measuredWidthRef.current) return;
      if (settleTimer !== null) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        settleTimer = null;
        const settled = content.getBoundingClientRect().width;
        if (settled === measuredWidthRef.current) return;
        measuredRef.current = new WeakSet();
        for (const child of content.children) {
          if (child instanceof HTMLElement && child.dataset.virtualTurn !== undefined) continue;
          // Only measurement-owned pins (`auto <px>`) are cleared; the
          // sentinels' literal `0px` stays.
          if (
            child instanceof HTMLElement &&
            child.style.containIntrinsicSize.startsWith('auto ')
          ) {
            child.style.containIntrinsicSize = '';
          }
        }
        measureNewRows();
      }, 150);
    });
    observer.observe(content);
    return () => {
      observer.disconnect();
      if (settleTimer !== null) window.clearTimeout(settleTimer);
    };
  }, [contentRef, measureNewRows]);
}
